/**
 * Card refunds (estornos) on Conciliação rápida: candidates, posting, pairing, undo, and ignore
 * with a reason. A refund posts as the reversal of its purchase; it is never revenue and never an
 * AP payment. Design brief: docs/design/card-refund-and-ignore-design-brief.md.
 */

/** What the refund can do with this purchase line. */
export type CardRefundCandidateAction =
  /** The purchase is posted: the refund posts as its reversal (R3). */
  | 'POST_REFUND'
  /** Both lines are pending, same amount, statement and month: cancel both, post nothing (R4). */
  | 'PAIR'
  /** The purchase is pending but cannot be paired: post the purchase first. */
  | 'POST_PURCHASE_FIRST'
  /** The purchase's bill waits for approval: the refund waits too. */
  | 'AWAITING_APPROVAL';

export interface CardRefundCandidate {
  itemId: string;
  postedAt: string;
  merchantName: string;
  /** BRL, positive, as the card charged it. */
  amount: string;
  foreignCurrencyCode: string | null;
  foreignAmount: string | null;
  /** BRL still open on the purchase after earlier active refunds. */
  remainingAmount: string;
  /** Foreign units still open, when the purchase is in a foreign currency. */
  remainingForeignAmount: string | null;
  last4: string | null;
  installmentNumber: number | null;
  installmentTotal: number | null;
  /** The refund line names the same installment ("AMAZON 12/12" refunds "AMAZON 12/12"). */
  sameInstallment: boolean;
  billId: string | null;
  /** The bill's human reference (BILL-004512). Never a UUID. */
  billReference: string | null;
  /** The refund amount equals what is open on the purchase. */
  exactAmount: boolean;
  action: CardRefundCandidateAction;
}

/**
 * One refund of a whole parcelado purchase: bigger than any one installment, so no single
 * candidate fits. Post it with every `installments[].itemId`; the refund fills the oldest first.
 */
export interface CardRefundInstallmentGroup {
  merchantName: string;
  installmentTotal: number;
  /** The day of the purchase, when the card provider sends it. */
  originalPurchaseAt: string | null;
  /** The posted installments of the purchase, oldest first. Each one has action POST_REFUND. */
  installments: CardRefundCandidate[];
  /** BRL still open across the installments. */
  remainingAmount: string;
  remainingForeignAmount: string | null;
  /** The refund equals what is open across the installments. */
  exactAmount: boolean;
}

export interface CardRefundCandidatesResponse {
  refundItemId: string;
  /**
   * Candidates, best first: exact amount, then the same installment, then closest date. Empty,
   * with no installment group either, means "Sem compra" (R5).
   */
  candidates: CardRefundCandidate[];
  /** Parcelado purchases the refund covers only across several installments. Best first. */
  installmentGroups: CardRefundInstallmentGroup[];
}

export interface PostCardRefundRequest {
  entityId: string;
  /** R3: the purchase line(s). Installments are filled oldest first. */
  purchaseItemIds?: string[];
  /** R5: no purchase. Vendor and splits are required together. */
  vendorId?: string;
  splits?: CardRefundSplitRequest[];
}

export interface CardRefundSplitRequest {
  glAccountId: string;
  /** BRL, two decimals. The splits sum to the refund exactly. */
  amount: string;
  costCenterId?: string;
  departmentId?: string;
  managerialAccountId?: string;
}

export interface PairCardRefundRequest {
  entityId: string;
  purchaseItemId: string;
}

export type CardRefundLinkKind = 'MATCHED' | 'PAIRED' | 'UNMATCHED';

export interface CardRefundPostingResponse {
  linkId: string;
  kind: CardRefundLinkKind;
  journalIds: string[];
  /** Realized FX in BRL (positive = gain). Null outside foreign-currency refunds. */
  fxDifferenceAmount: string | null;
  alreadyPosted: boolean;
}

export type CardRefundPreviewLineKind = 'CARD' | 'EXPENSE' | 'FX_GAIN' | 'FX_LOSS';

/** R3 confirm dialog: the lines the refund will post, per book. Nothing is written. */
export interface CardRefundPreview {
  /** ISO 4217 code of every amount here: the card's billing currency (the books' functional one). */
  currencyCode: string;
  /** Positive, in `currencyCode`. */
  refundAmount: string;
  /** Realized FX in BRL (positive = gain). Null outside foreign-currency refunds. */
  fxDifferenceAmount: string | null;
  /** YYYY-MM-DD: the journal date (the refund line's date). */
  postingDate: string;
  books: {
    bookId: string;
    bookCode: string | null;
    bookName: string;
    lines: {
      side: 'DEBIT' | 'CREDIT';
      kind: CardRefundPreviewLineKind;
      glAccountId: string;
      accountCode: string | null;
      accountName: string | null;
      /** BRL, positive. */
      amount: string;
      /** The purchase line this credit reverses (EXPENSE lines only). */
      purchaseItemId: string | null;
      costCenterName: string | null;
    }[];
  }[];
}

export interface PreviewCardRefundRequest {
  entityId: string;
  purchaseItemIds: string[];
}

export interface UndoCardRefundRequest {
  entityId: string;
  /** YYYY-MM-DD. Defaults to today; moves forward to the next open period when needed. */
  reversalDate?: string;
}

export interface UndoCardRefundResponse {
  linkId: string;
  reversalJournalIds: string[];
}

/** R7. The fixed reasons the user picks from. */
export const CARD_IGNORE_REASONS = ['DUPLICATE', 'POSTED_ELSEWHERE', 'OTHER'] as const;
export type CardIgnoreReason = (typeof CARD_IGNORE_REASONS)[number];

export interface IgnoreCardItemsRequest {
  entityId: string;
  itemIds: string[];
  reason: CardIgnoreReason;
  /** Required for OTHER. */
  notes?: string;
  /** Required for POSTED_ELSEWHERE: the card-paid bill that already booked the purchase. */
  relatedBillId?: string;
}

export interface RestoreCardItemsRequest {
  entityId: string;
  itemIds: string[];
}

export interface CardItemBatchResult {
  succeeded: string[];
  failed: { itemId: string; code: string; message: string }[];
}

export type CardRefundListState = 'OPEN' | 'POSTED' | 'PAIRED' | 'HANDLED_ELSEWHERE';

/** The "Estornos" tab: one refund line of the card, with what it was posted against. */
export interface CardRefundListItem {
  itemId: string;
  postedAt: string;
  merchantName: string;
  /** Positive, in `currencyCode`. */
  amount: string;
  /** ISO 4217 code of the card's billing currency: the currency of `amount`. */
  currencyCode: string;
  foreignCurrencyCode: string | null;
  foreignAmount: string | null;
  last4: string | null;
  /**
   * OPEN: nothing done yet. POSTED / PAIRED: an active refund link exists. HANDLED_ELSEWHERE: no
   * refund link, but the line was settled another way (see `handledBy`); no action here.
   */
  state: CardRefundListState;
  /** HANDLED_ELSEWHERE only: the Onfly refund worker, or the reconciliation screen. */
  handledBy: 'ONFLY' | 'RECONCILIATION' | null;
  /** An IOF refund reverses an IOF fee line; any other refund reverses a purchase. */
  lineType: 'PURCHASE_REFUND' | 'IOF_REFUND';
  link: CardRefundLinkSummary | null;
  /** OPEN refunds only: what the system found. Null when not computed (see the list response). */
  match: CardRefundMatch | null;
}

/**
 * The purchase the system found for an open refund, so each row shows its state with no extra call.
 * FOUND: exactly one candidate has the exact amount ("Compra encontrada", or "Compra pendente"
 * when its action is PAIR). CHOOSE: candidates, none clearly best ("Escolha a compra").
 * NONE: no candidate ("Sem compra", R5).
 */
export interface CardRefundMatch {
  status: 'FOUND' | 'CHOOSE' | 'NONE';
  /**
   * Best first, at most 3. The full list is `GET card-refunds/:itemId/candidates`. FOUND through
   * an installment group: the group's installments, all of them.
   */
  candidates: CardRefundCandidate[];
  /** FOUND through an installment group: post the refund with all `candidates`. Else null. */
  installmentGroup: CardRefundInstallmentGroup | null;
  /** Single candidates plus installment groups. */
  candidateCount: number;
}

export interface CardRefundLinkSummary {
  linkId: string;
  kind: CardRefundLinkKind;
  postingDate: string;
  fxDifferenceAmount: string | null;
  /** R5: the vendor the user picked for a refund with no purchase. */
  vendorName: string | null;
  purchases: {
    itemId: string;
    postedAt: string;
    merchantName: string;
    /** BRL this refund took off the purchase. */
    amount: string;
    installmentNumber: number | null;
    installmentTotal: number | null;
    billId: string | null;
    billReference: string | null;
  }[];
  /** False when nothing was posted (PAIRED). */
  hasJournal: boolean;
}

export interface CardRefundListResponse {
  /** One page of refund lines, newest first. */
  items: CardRefundListItem[];
  /** 1-based. */
  page: number;
  pageSize: number;
  /** Refund lines in the filter, on every page. */
  total: number;
  /** OPEN refunds in the filter, on every page. HANDLED_ELSEWHERE lines are not counted. */
  openCount: number;
  /** Sum of the OPEN refunds in the filter, on every page, one entry per card currency. */
  openAmounts: CardRefundCurrencyAmount[];
  /** False when this page had too many open refunds to match them all; rows with `match: null`. */
  matchesComplete: boolean;
}

export interface CardRefundCurrencyAmount {
  currencyCode: string;
  /** Positive. */
  amount: string;
}

export interface CardIgnoredItemsResponse {
  /** One page of ignored lines, newest first. */
  items: CardIgnoredItem[];
  /** 1-based. */
  page: number;
  pageSize: number;
  /** Ignored lines in the filter, on every page. */
  total: number;
  /**
   * Sum of the ignored lines in the filter, on every page, one entry per card currency, positive
   * for purchases (the fatura tie-out). Lines of a BRL card and a USD card are never added up.
   */
  totalAmounts: CardRefundCurrencyAmount[];
}

export interface CardIgnoredItem {
  itemId: string;
  postedAt: string;
  merchantName: string;
  /** Signed, in the card's currency, as the card shows it (negative = purchase). */
  amount: string;
  /** The card's billing currency. */
  currencyCode: string;
  last4: string | null;
  reason: CardIgnoreReason | null;
  notes: string | null;
  relatedBillId: string | null;
  relatedBillReference: string | null;
  ignoredAt: string | null;
  ignoredByName: string | null;
}
